/* Copyright (c) 2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { Container, Row, Col, Box, Heading, Loader } from "@nordcloud/gnui";
import { CustomerBasic } from "~/generated/graphql";
import { Table } from "~/components";
import { ErrorMessage } from "~/components/Error/ErrorMessage";
import { client } from "~/services/Auth/apollo/ApolloProvider";
import { CustomerItem } from "~/views/customers/AddCustomer/CustomerItem";
import { useGetCustomers } from "~/views/customers/hooks/useGetCustomers";
import { CustomersListColumns } from "./CustomersList";

type CustomersPageProps = {
  apolloClient?: ApolloClient<NormalizedCacheObject>;
};

function CustomersPage({ apolloClient = client }: CustomersPageProps) {
  const [rows, setRows] = React.useState<Array<CustomerBasic>>();

  const { customers, loading, error, refetch } = useGetCustomers({
    client: apolloClient,
  });

  React.useEffect(() => {
    setRows(customers);
  }, [customers]);

  const columns = React.useMemo(
    () =>
      CustomersListColumns({
        refetch: refetch,
      }),
    [refetch]
  );

  return (
    <Container>
      <Row>
        <Col>
          <Box
            css={{ display: "flex", alignItems: "center" }}
            spacing="spacing04"
          >
            <Heading level={2}>List of Customers</Heading>
            <CustomerItem refetch={refetch} />
          </Box>
          {error && <ErrorMessage error={error} />}
          <Box spacing="spacing04">
            {loading ? (
              <Loader inContent />
            ) : (
              <>
                <Table columns={columns} data={rows || []} />
              </>
            )}
          </Box>
        </Col>
      </Row>
    </Container>
  );
}

export { CustomersPage };
